import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { Truck, Store, ShieldCheck, Lock } from "lucide-react";
import { api, money, formatApiError } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { GorillaGuarantee } from "@/components/GorillaGuarantee";
import { toast } from "sonner";

export default function Checkout() {
  const { items, subtotal, clearCart } = useCart();
  const navigate = useNavigate();
  const [config, setConfig] = useState({ shipping_flat_rate: 8, paypal_configured: false, stripe_configured: false, card_payments_enabled: false, paypal_client_id: "", currency: "USD" });
  const [fulfillment, setFulfillment] = useState("shipping");
  const [paymentMethod, setPaymentMethod] = useState("stripe");
  const [placedOrder, setPlacedOrder] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [quote, setQuote] = useState({ shipping_cost: 0, tax: 0, tax_rate: 0, total: 0, weight_oz: 0, service: "USPS Ground Advantage" });
  const [quoteError, setQuoteError] = useState(false);
  const [form, setForm] = useState({
    name: "", email: "", phone: "",
    line1: "", line2: "", city: "", state: "", postal_code: "", note: "",
  });

  useEffect(() => {
    api.get("/config").then((r) => {
      setConfig(r.data);
      if (r.data.card_payments_enabled) setPaymentMethod("stripe");
      else if (r.data.paypal_configured) setPaymentMethod("paypal");
      else setPaymentMethod("invoice");
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (items.length === 0) return;
    const t = setTimeout(() => {
      const body = {
        items: items.map((i) => ({ product_id: i.product_id, name: i.name, price: i.price, quantity: i.quantity, variant_vid: i.variant_vid, variant_label: i.variant_label })),
        fulfillment,
        state: form.state,
        postal_code: form.postal_code,
        city: form.city,
      };
      api.post("/shipping/quote", body)
        .then((r) => { setQuote(r.data); setQuoteError(false); })
        .catch(() => setQuoteError(true));
    }, 350);
    return () => clearTimeout(t);
  }, [items, fulfillment, form.state, form.postal_code, form.city]);

  useEffect(() => {
    if (items.length === 0 && !placedOrder) navigate("/cart");
  }, [items, placedOrder, navigate]);

  const shippingCost = fulfillment === "pickup" ? 0 : Number(quote.shipping_cost || 0);
  const taxAmount = Number(quote.tax || 0);
  const total = subtotal + shippingCost + taxAmount;
  const shippingUnavailable = fulfillment === "shipping" && quoteError;
  const effectivePayment = paymentMethod;
  const shippingAllowed = quote.shipping_allowed !== false;
  const pickupAllowed = quote.pickup_allowed !== false;

  useEffect(() => {
    if (fulfillment === "shipping" && !shippingAllowed && pickupAllowed) setFulfillment("pickup");
    if (fulfillment === "pickup" && !pickupAllowed && shippingAllowed) setFulfillment("shipping");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shippingAllowed, pickupAllowed]);

  const paypalOptions = useMemo(
    () => ({ clientId: config.paypal_client_id, currency: config.currency }),
    [config.paypal_client_id, config.currency]
  );

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const buildPayload = (method) => ({
    items: items.map((i) => ({ product_id: i.product_id, name: i.name, price: i.price, quantity: i.quantity, image: i.image, variant_vid: i.variant_vid, variant_label: i.variant_label })),
    customer: { name: form.name, email: form.email, phone: form.phone },
    fulfillment,
    shipping_address: fulfillment === "shipping"
      ? { line1: form.line1, line2: form.line2, city: form.city, state: form.state, postal_code: form.postal_code, country: "US" }
      : null,
    payment_method: method,
    note: form.note,
  });

  const validate = () => {
    if (!form.name || !form.email) return "Please enter your name and email.";
    if (fulfillment === "shipping" && (!form.line1 || !form.city || !form.state || !form.postal_code))
      return "Please complete your shipping address.";
    return null;
  };

  const placeOrder = async (method) => {
    const err = validate();
    if (err) { toast.error(err); return null; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/orders", buildPayload(method));
      setPlacedOrder(data);
      return data;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      return null;
    } finally {
      setSubmitting(false);
    }
  };

  const handleNonPaypal = async () => {
    const order = await placeOrder(effectivePayment);
    if (order) {
      clearCart();
      navigate(`/order/${order.order_number}`);
    }
  };

  const handleStripe = async () => {
    const order = await placeOrder("stripe");
    if (!order) return;
    try {
      const { data } = await api.post("/payments/stripe/checkout", {
        order_number: order.order_number, origin_url: window.location.origin,
      });
      window.location.href = data.url;   // redirect to Stripe-hosted checkout
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not start card checkout.");
    }
  };

  const showPaypal = paymentMethod === "paypal" && config.paypal_configured;
  const showStripe = paymentMethod === "stripe" && config.card_payments_enabled;

  return (
    <div data-testid="checkout-page" className="jm-container py-12">
      <h1 className="font-display text-4xl sm:text-5xl text-navy">Checkout</h1>

      <div className="mt-8 grid gap-10 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-8">
          {/* Contact */}
          <section className="bg-white rounded-lg border border-navy/15 p-6">
            <h2 className="font-display text-2xl text-navy">Contact details</h2>
            <div className="mt-4 grid sm:grid-cols-2 gap-4">
              <input data-testid="co-name" placeholder="Full name *" value={form.name} onChange={set("name")} className="rounded-md border border-navy/20 px-4 py-3 outline-none focus:border-brick" />
              <input data-testid="co-email" type="email" placeholder="Email *" value={form.email} onChange={set("email")} className="rounded-md border border-navy/20 px-4 py-3 outline-none focus:border-brick" />
              <input data-testid="co-phone" placeholder="Phone (optional)" value={form.phone} onChange={set("phone")} className="rounded-md border border-navy/20 px-4 py-3 outline-none focus:border-brick sm:col-span-2" />
            </div>
          </section>

          {/* Fulfillment */}
          <section className="bg-white rounded-lg border border-navy/15 p-6">
            <h2 className="font-display text-2xl text-navy">Delivery method</h2>
            <div className="mt-4 grid sm:grid-cols-2 gap-4">
              <button
                data-testid="fulfillment-shipping"
                onClick={() => shippingAllowed && setFulfillment("shipping")}
                disabled={!shippingAllowed}
                className={`flex items-start gap-3 rounded-lg border-2 p-4 text-left transition-colors ${!shippingAllowed ? "opacity-40 cursor-not-allowed border-navy/10" : fulfillment === "shipping" ? "border-brick bg-cream" : "border-navy/15 hover:border-navy/40"}`}
              >
                <Truck className="w-6 h-6 text-brick shrink-0" />
                <div>
                  <div className="font-bold text-navy">Ship to me</div>
                  <div className="text-sm text-ink/60">{shippingAllowed ? "USPS — calculated by weight" : "Not available for an item in your cart"}</div>
                </div>
              </button>
              <button
                data-testid="fulfillment-pickup"
                onClick={() => pickupAllowed && setFulfillment("pickup")}
                disabled={!pickupAllowed}
                className={`flex items-start gap-3 rounded-lg border-2 p-4 text-left transition-colors ${!pickupAllowed ? "opacity-40 cursor-not-allowed border-navy/10" : fulfillment === "pickup" ? "border-brick bg-cream" : "border-navy/15 hover:border-navy/40"}`}
              >
                <Store className="w-6 h-6 text-brick shrink-0" />
                <div>
                  <div className="font-bold text-navy">Local pickup</div>
                  <div className="text-sm text-ink/60">{pickupAllowed ? "$0 shipping — pay online now, pick up in Ponte Vedra Beach, FL" : "Not available for an item in your cart"}</div>
                </div>
              </button>
            </div>

            {fulfillment === "shipping" && (
              <div className="mt-5 grid sm:grid-cols-2 gap-4">
                <input data-testid="co-line1" placeholder="Address *" value={form.line1} onChange={set("line1")} className="rounded-md border border-navy/20 px-4 py-3 outline-none focus:border-brick sm:col-span-2" />
                <input data-testid="co-line2" placeholder="Apt, suite (optional)" value={form.line2} onChange={set("line2")} className="rounded-md border border-navy/20 px-4 py-3 outline-none focus:border-brick sm:col-span-2" />
                <input data-testid="co-city" placeholder="City *" value={form.city} onChange={set("city")} className="rounded-md border border-navy/20 px-4 py-3 outline-none focus:border-brick" />
                <input data-testid="co-state" placeholder="State *" value={form.state} onChange={set("state")} className="rounded-md border border-navy/20 px-4 py-3 outline-none focus:border-brick" />
                <input data-testid="co-zip" placeholder="ZIP *" value={form.postal_code} onChange={set("postal_code")} className="rounded-md border border-navy/20 px-4 py-3 outline-none focus:border-brick" />
              </div>
            )}
            {fulfillment === "pickup" && (
              <p data-testid="pickup-pay-note" className="mt-4 text-sm text-ink/70">
                <b className="text-navy">Pay securely online now</b> — your item is reserved the moment payment completes. We'll email you to arrange a pickup time.
              </p>
            )}
          </section>

          {/* Payment */}
          <section className="bg-white rounded-lg border border-navy/15 p-6">
            <h2 className="font-display text-2xl text-navy flex items-center gap-2">
              <Lock className="w-5 h-5 text-brick" /> Payment
            </h2>

            {(config.card_payments_enabled || config.paypal_configured) ? (
              <div className="mt-4">
                <div className="flex flex-wrap gap-3">
                  {config.card_payments_enabled && (
                    <button
                      data-testid="pay-card"
                      onClick={() => setPaymentMethod("stripe")}
                      className={`rounded-md border-2 px-4 py-2 font-bold ${paymentMethod === "stripe" ? "border-brick bg-cream text-navy" : "border-navy/15 text-navy/70"}`}
                    >
                      Credit / Debit Card
                    </button>
                  )}
                  {config.paypal_configured && (
                    <button
                      data-testid="pay-paypal"
                      onClick={() => setPaymentMethod("paypal")}
                      className={`rounded-md border-2 px-4 py-2 font-bold ${paymentMethod === "paypal" ? "border-brick bg-cream text-navy" : "border-navy/15 text-navy/70"}`}
                    >
                      PayPal
                    </button>
                  )}
                </div>
                {!config.card_payments_enabled && config.stripe_configured && (
                  <p data-testid="card-unavailable-notice" className="mt-3 text-sm font-semibold text-brick">
                    Credit/debit card checkout is temporarily unavailable. Please use PayPal.
                  </p>
                )}
                <p className="mt-3 text-sm text-ink/60">
                  {paymentMethod === "stripe"
                    ? "You'll be taken to a secure card checkout — Apple Pay is available on Safari / iPhone."
                    : "You'll complete payment securely through PayPal."}
                </p>
                <div data-testid="payment-badges" className="mt-4 flex flex-wrap items-center gap-2">
                  {["Apple Pay", "Visa", "Mastercard", "Amex", "Discover", "PayPal"].map((m) => (
                    <span key={m} className="text-[11px] font-bold uppercase tracking-wide rounded border border-navy/20 bg-cream/60 text-navy px-2 py-1">{m}</span>
                  ))}
                  <span className="inline-flex items-center gap-1 text-[11px] font-bold text-navy/60"><Lock className="w-3 h-3" /> Encrypted checkout</span>
                </div>
              </div>
            ) : (
              <div data-testid="payment-not-configured" className="mt-3 rounded-md bg-cream border border-navy/15 p-4 text-sm text-ink/75">
                Online payments are being set up. Place your order now and we'll email you a secure payment link to complete your purchase.
              </div>
            )}

            <textarea
              data-testid="co-note"
              placeholder="Order note (optional)"
              value={form.note}
              onChange={set("note")}
              rows={2}
              className="mt-4 w-full rounded-md border border-navy/20 px-4 py-3 outline-none focus:border-brick resize-none"
            />
          </section>

          <GorillaGuarantee compact />
        </div>

        {/* Summary */}
        <div className="bg-cream border border-navy/15 rounded-lg p-6 h-fit lg:sticky lg:top-24">
          <h2 className="font-display text-2xl text-navy">Order summary</h2>
          <div className="mt-4 space-y-3 max-h-64 overflow-auto">
            {items.map((i) => (
              <div key={`${i.product_id}-${i.variant_vid || ""}`} className="flex justify-between text-sm">
                <span className="text-ink/80">{i.name}{i.variant_label ? ` (${i.variant_label})` : ""} × {i.quantity}</span>
                <span className="font-semibold">{money(i.price * i.quantity)}</span>
              </div>
            ))}
          </div>
          <div className="mt-4 pt-4 border-t border-navy/15 space-y-2 text-ink/80">
            <div className="flex justify-between"><span>Subtotal</span><span data-testid="summary-subtotal" className="font-semibold">{money(subtotal)}</span></div>
            <div className="flex justify-between"><span>{fulfillment === "pickup" ? "Shipping (Local Pickup)" : (quote.service || "USPS Ground Advantage")}</span><span data-testid="summary-shipping" className="font-semibold">{fulfillment === "pickup" ? "$0.00" : shippingCost === 0 ? "Free" : money(shippingCost)}</span></div>
            {taxAmount > 0 && (
              <div className="flex justify-between"><span>Sales tax{quote.tax_rate ? ` (${(quote.tax_rate * 100).toFixed(1)}%)` : ""}</span><span data-testid="summary-tax" className="font-semibold">{money(taxAmount)}</span></div>
            )}
            <div className="flex justify-between font-display text-xl text-navy pt-2"><span>Total</span><span data-testid="summary-total">{money(total)}</span></div>
            {fulfillment === "shipping" && taxAmount === 0 && (form.state || "").trim() === "" && (
              <p className="text-xs text-ink/50">Sales tax is calculated from your shipping address.</p>
            )}
          </div>

          {shippingUnavailable && (
            <div data-testid="shipping-error" className="mt-4 rounded-md border border-brick/40 bg-brick/5 p-3 text-sm font-semibold text-brick">
              We couldn't calculate shipping for your cart. Please check your items and try again — orders can't be placed without a shipping cost.
            </div>
          )}

          <div className="mt-6">
            {showPaypal ? (
              <PayPalScriptProvider options={paypalOptions}>
                <PayPalButtons
                  style={{ layout: "vertical", color: "gold", shape: "rect" }}
                  createOrder={async () => {
                    const { data } = await api.post("/paypal/create-order", buildPayload("paypal"));
                    return data.id;
                  }}
                  onApprove={async (data) => {
                    const order = placedOrder || (await placeOrder("paypal"));
                    if (!order) return;
                    await api.post(`/paypal/capture/${data.orderID}?order_number=${order.order_number}`);
                    clearCart();
                    navigate(`/order/${order.order_number}`);
                  }}
                  onError={() => toast.error("Payment could not be completed. Please try again.")}
                />
              </PayPalScriptProvider>
            ) : showStripe ? (
              <button
                data-testid="pay-card-btn"
                disabled={submitting || shippingUnavailable}
                onClick={handleStripe}
                className="w-full bg-brick text-white font-bold uppercase rounded-md px-6 py-3.5 hover:bg-navy transition-colors disabled:opacity-60"
              >
                {submitting ? "Starting checkout..." : `Pay ${money(total)} by Card`}
              </button>
            ) : (
              <button
                data-testid="place-order-btn"
                disabled={submitting || shippingUnavailable}
                onClick={handleNonPaypal}
                className="w-full bg-brick text-white font-bold uppercase rounded-md px-6 py-3.5 hover:bg-navy transition-colors disabled:opacity-60"
              >
                {submitting ? "Placing order..." : "Place Order"}
              </button>
            )}
          </div>
          <p className="mt-4 flex items-center gap-2 text-xs text-ink/50">
            <ShieldCheck className="w-4 h-4 text-brick" /> Protected by The Gorilla Guarantee
          </p>
        </div>
      </div>
    </div>
  );
}
